import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, LogIn, ShieldCheck } from 'lucide-react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { BrandLink, Signature } from '@/components/Brand';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/features/auth/AuthContext';
import { ApiError } from '@/services/api';
import { fetchers, queryKeys } from '@/services/queries';

export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const providers = useQuery({ queryKey: queryKeys.providers, queryFn: fetchers.providers });

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/app'} replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/app', { replace: true });
    } catch (caught) {
      // The API returns one generic message so that accounts cannot be enumerated.
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Connexion impossible. Vérifiez que le service est disponible.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isDevEnvironment =
    providers.data?.environment === 'development' || providers.data?.environment === 'preprod';

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-8 flex justify-center">
        <BrandLink />
      </div>

      <Card>
        <CardBody className="p-6">
          <h1 className="text-xl font-bold tracking-tight">Connexion à votre espace</h1>
          <p className="mt-1.5 text-sm text-muted">
            Accédez à vos parcours, travaux pratiques et résultats.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
            <Input
              label="Adresse e-mail"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="prenom.nom@exemple.fr"
            />
            <Input
              label="Mot de passe"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            {error ? (
              <Alert tone="danger" title="Échec de la connexion">
                {error}
              </Alert>
            ) : null}

            <Button type="submit" size="lg" className="w-full" disabled={submitting}>
              {submitting ? <Spinner /> : <LogIn className="h-4 w-4" />}
              {submitting ? 'Connexion…' : 'Se connecter'}
            </Button>
          </form>

          <p className="mt-5 flex items-start gap-2 text-xs text-faint">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Authentification vérifiée côté serveur. Les droits sont appliqués par l'API sur chaque
            ressource, jamais par le navigateur.
          </p>
        </CardBody>
      </Card>

      {isDevEnvironment ? (
        <Alert tone="info" title="Environnement de développement" className="mt-6">
          <p className="text-xs">
            Des comptes de démonstration sont provisionnés par{' '}
            <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-2xs">
              python -m app.content.seed --with-demo
            </code>
            . Ils sont refusés en production.
          </p>
        </Alert>
      ) : null}

      {providers.data && !providers.data.providers.includes('local') ? (
        <Alert tone="warning" title="Authentification locale désactivée" className="mt-6">
          Cet environnement utilise un fournisseur d'identité externe.
        </Alert>
      ) : null}

      <p className="mt-6 flex items-start gap-2 text-xs text-faint">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        L'intégration avec EFILearn (redirection sécurisée puis vérification OTP par e-mail) est
        prévue dans une phase ultérieure et n'est pas encore active.
      </p>

      <div className="mt-8 text-center">
        <Link to="/" className="text-sm text-muted hover:text-fg">
          ← Retour au site
        </Link>
      </div>
      <Signature className="mt-6 text-center" />
    </div>
  );
}
